import api from './api';

/**
 * Client for the Invoice Estimate Report — the open book's current invoice totals
 * against the projected ones, aggregated from the reconciler sweep's estimate cache.
 *
 * The per-PRO version of this is the "Invoice Estimate" modal on the Pro Invoicing tab.
 * This is the same projection asked of every open order at once, so the question becomes
 * "where are we about to under-bill", answerable by customer, bill-to or line item.
 *
 * THE SPLIT THAT MATTERS. `deltaTotal` (projected - current) is NOT the exposure: most of
 * it is lines on orders nobody has rated yet, which will rate themselves at billing time.
 * `deltaActionable` (missing + drift - overbilled) is the number worth acting on, and the
 * two are returned side by side so they can never be mistaken for each other.
 */

/** WHAT is wrong with the line. Orthogonal to EstimateState. */
export type EstimateKind =
  | 'missing'     // the invoice bills none of it: no line, or a line sitting at $0
  | 'drift'       // the line belongs there; only its rate disagrees with the quote
  | 'overbilled'  // billed, but the quote's terms don't support it here
  | 'withheld'    // an override deliberately declined to bill it
  | 'agreed';     // invoice and estimate already agree

/**
 * WHETHER THE AMOUNT CAN BE TRUSTED YET — a property of the LINE, not the order. One PRO
 * can have fuel completed (its rate keys off a delivery that happened) while its chassis
 * fee is still pending (that keys off a terminate date it hasn't reached), so a PRO shows
 * in both states and PRO counts must never be summed across them.
 */
export type EstimateState = 'completed' | 'pending';

export type EstimateGroupBy =
  | 'customer' | 'billto' | 'liitem' | 'kind' | 'state' | 'movetype';

export interface EstimateMeasures {
  pros: number;
  line_count: number;
  current_total: number;
  projected_total: number;
  delta_total: number;
  missing_total: number;
  drift_total: number;
  overbilled_total: number;
  withheld_total: number;
  /** Lines the estimate proposes touching, money or not — what makes a $0 row legible. */
  change_lines: number;
  /**
   * PROs carrying at least one of those lines. `pros` counts every PRO with a projected
   * line, agreed ones included; this is what the drill-down opens on in the default view,
   * and what the PROs column shows unless matched invoices are included.
   */
  changed_pros: number;
  /** Every date-assumed line, whatever is wrong with it — the provisional money. */
  pending_total: number;
  pending_lines: number;
  missing_lines: number;
  drift_lines: number;
  overbilled_lines: number;
  /** missing + drift - overbilled. What the page headlines. */
  delta_actionable: number;
}

export interface EstimateGroupRow extends EstimateMeasures {
  group_key: string | number | null;
  group_label: string;
}

export interface EstimateTotals extends EstimateMeasures {
  synced_at: string | null;
  estimate_as_of: string | null;
}

export interface EstimateSummaryResponse {
  groupBy: EstimateGroupBy;
  rows: EstimateGroupRow[];
  totals: EstimateTotals;
}

export interface EstimateDetailRow {
  pronumber: number;
  customercode: number;
  customer: string;
  billto_recno: number;
  billto: string;
  movetype: string;
  liitem: string;
  gl_type: string;
  kind: EstimateKind;
  qty: number;
  rate: number;
  ratetype: string;
  projected: number;
  current_qty: number | null;
  current_rate: number | null;
  current_ratetype: string | null;
  current_total: number;
  delta: number;
  /** What the override declined to bill: qty x rate. Current/Suggested are $0 on these
   *  lines by design, so this is the only place the forgone amount appears. */
  withheld_qty: number | null;
  withheld_rate: number | null;
  withheld_total: number | null;
  estimated: 'Y' | 'N';
  /** Derived from `estimated` — 'pending' when a date had to be assumed. */
  state?: EstimateState;
  estimate_basis: string;
  reason: string;
  description: string;
  accessorialnum: number;
  estimate_as_of: string | null;
  synced_at: string | null;
  /**
   * The recorded decision not to bill this charge (cms.invoice_lineitem_override).
   * Null when no override covers this PRO + line-item type. Overrides are keyed by TYPE,
   * so a reason can be present on a line the estimate is otherwise proposing normally.
   */
  override_reason: string | null;
  override_by: string | null;
  override_at: string | null;
  /**
   * Any orders-registry field the user added to the grid arrives as a further key here,
   * named by the field's API name (`getApiFieldName`), repeated on every line of the
   * same PRO. Not declared individually: the set is whatever the registry offers.
   */
  [addedField: string]: unknown;
}

export interface EstimateDetailResponse {
  rows: EstimateDetailRow[];
  /** Rows actually returned. */
  count: number;
  /** Rows matching the filters BEFORE the limit — so a truncated view can say so. */
  total: number;
  truncated: boolean;
}

export interface EstimateFilterOption {
  value: string | number;
  label: string;
  pros: number;
}

export interface EstimateFilterOptions {
  customers: EstimateFilterOption[];
  billtos: EstimateFilterOption[];
  lineItems: EstimateFilterOption[];
  kinds: EstimateKind[];
}

export interface EstimateMeta {
  pros: number;
  skipped: number;
  synced_at: string | null;
  estimate_as_of: string | null;
}

export interface EstimateQuery {
  customer?: string[];
  billto?: string[];
  liitem?: string[];
  kind?: EstimateKind[];
  state?: EstimateState[];
  /** Move types as the axis keys them — '' is an order with none. */
  movetype?: string[];
  /**
   * Also include orders that cannot move yet: imports and exports with no GWI Available
   * date, other move types whose originate leg has no appointment. Off by default —
   * their charges are not yet due, and they are most of the open book.
   */
  showNotAvailable?: boolean;
  /** Delivery-leg arrival on/after this (YYYY-MM-DD). */
  deliveredFrom?: string;
  /**
   * Delivery-leg arrival on/before this (YYYY-MM-DD). Leaving it undefined alongside a
   * `deliveredFrom` ALSO returns containers that have not delivered yet — 72% of the open
   * book. Both undefined applies no date filter at all: every open invoice.
   */
  deliveredTo?: string;
}

const BASE = '/invoice-estimate-report';

/** Array filters go up as comma-separated lists; empty arrays are omitted entirely. */
function toParams(q: EstimateQuery): Record<string, string> {
  const p: Record<string, string> = {};
  if (q.customer?.length) p.customer = q.customer.join(',');
  if (q.billto?.length) p.billto = q.billto.join(',');
  if (q.liitem?.length) p.liitem = q.liitem.join(',');
  if (q.kind?.length) p.kind = q.kind.join(',');
  if (q.state?.length) p.state = q.state.join(',');
  if (q.movetype?.length) p.movetype = q.movetype.join(',');
  if (q.showNotAvailable) p.includeNotAvailable = 'true';
  if (q.deliveredFrom) p.deliveredFrom = q.deliveredFrom;
  if (q.deliveredTo) p.deliveredTo = q.deliveredTo;
  return p;
}

/**
 * The page query narrowed to ONE group: the group's own key becomes a filter on top of
 * everything already set, so a drill-down shows exactly the lines behind the number
 * that was clicked — never the whole unfiltered set.
 *
 * Exhaustive over EstimateGroupBy on purpose. An axis without a case here is a compile
 * error, not a drill-down that quietly ignores its group — which is how a Move Type row
 * of 1 PRO (INBOUND) opened to 1,876 of them.
 */
export function scopeQueryToGroup(
  query: EstimateQuery,
  groupBy: EstimateGroupBy,
  groupKey: string | number | null,
): EstimateQuery {
  const key = groupKey == null ? '' : String(groupKey);
  switch (groupBy) {
    case 'customer': return { ...query, customer: [key] };
    case 'billto': return { ...query, billto: [key] };
    case 'liitem': return { ...query, liitem: [key] };
    case 'kind': return { ...query, kind: [key as EstimateKind] };
    case 'state': return { ...query, state: [key as EstimateState] };
    case 'movetype': return { ...query, movetype: [key] };
    default: {
      const unhandled: never = groupBy;
      throw new Error(`No drill-down filter for group-by ${String(unhandled)}`);
    }
  }
}

export const invoiceEstimateReportService = {
  /**
   * Grouped totals. Groups the estimate proposes NO change to are excluded server-side
   * unless `includeAgreed` — otherwise a customer fully in agreement (216 PROs, nothing
   * wrong) outranks the single line a card filter was pointing at.
   */
  async summary(
    groupBy: EstimateGroupBy,
    query: EstimateQuery = {},
    limit = 200,
    includeAgreed = false,
    /** True on the supporting calls a single view fires alongside its main one — the card
     *  totals and the line-item cards. They are the same endpoint serving a different part
     *  of one page, not a second report run, and without this each view files two or three
     *  rows into the run audit instead of one. */
    background = false,
  ): Promise<EstimateSummaryResponse> {
    const { data } = await api.get(`${BASE}/summary`, {
      params: {
        groupBy, limit,
        ...(includeAgreed ? { includeAgreed: true } : {}),
        ...(background ? { background: true } : {}),
        ...toParams(query),
      },
    });
    return data;
  },

  /**
   * Lines behind a group. Lines already in agreement are excluded server-side unless
   * `includeAgreed` — Home Depot has 10,783 projected lines and only 985 that differ, so
   * including them buries the discrepancies you opened the drill-down to find.
   *
   * `additionalFields` are orders-registry fieldnames to attach to every line as extra
   * keys (the `useAdditionalFields` contract); they go up comma-joined as
   * `additional_fields`, the way every other pro grid sends them.
   */
  async detail(
    query: EstimateQuery = {},
    opts: { includeAgreed?: boolean; limit?: number; additionalFields?: string[] } = {},
  ): Promise<EstimateDetailResponse> {
    const { data } = await api.get(`${BASE}/detail`, {
      params: {
        limit: opts.limit ?? 2000,
        ...(opts.includeAgreed ? { includeAgreed: true } : {}),
        ...(opts.additionalFields?.length
          ? { additional_fields: opts.additionalFields.join(',') }
          : {}),
        ...toParams(query),
      },
    });
    return {
      rows: data.rows ?? [],
      count: data.count ?? 0,
      total: data.total ?? 0,
      truncated: !!data.truncated,
    };
  },

  async filterOptions(): Promise<EstimateFilterOptions> {
    const { data } = await api.get(`${BASE}/filters`);
    return data;
  },

  async meta(): Promise<EstimateMeta> {
    const { data } = await api.get(`${BASE}/meta`);
    return data;
  },
};

export default invoiceEstimateReportService;
